import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { ActionsMenu, spacing } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { withTranslation } from '../../../../../locales/hoc';
import { TeamMemberDataType, TeamMembersTable } from '../TeamMembersTable';
import { AssignButton } from '../AssignButton';
import { useRemoveMemberFromTeam } from '../useRemoveMemberFromTeam';
import { PressableCell } from '../../../../components/PressableCell';
import { useGoToProfile } from '../../../../components/useGoToProfile';

interface Props {
	data: TeamMemberDataType[];
	organisationUuid: string;
	teamUuid: string;
	viewOnly?: boolean;
}

export const TeamCareRecipientsTable = withTranslation(
	({ data, organisationUuid, teamUuid, viewOnly = false }: Props) => {
		const { t } = useTranslation();
		const goToProfile = useGoToProfile();
		const { removeMemberFromTeam } = useRemoveMemberFromTeam(teamUuid);

		const renderRowActions = useCallback(
			({ row }: { row: TeamMemberDataType }) => (
				<ActionsMenu
					actions={[
						{
							title: t('table.actions.view-profile'),
							onPress: () => goToProfile(row),
						},
						{
							title: t('table.actions.remove-from-team'),
							onPress: removeMemberFromTeam(row),
							hidden: viewOnly,
						},
					]}
				/>
			),
			[viewOnly, t, removeMemberFromTeam, goToProfile],
		);

		const columnsConfig = useMemo(
			() => [
				{
					name: t('table.header.name'),
					renderCell: ({ row }: { row: Props['data'][0] }) => (
						<PressableCell onPress={() => goToProfile(row)} text={getName(row)} />
					),
				},
				{
					name: t('table.header.username'),
					accessor: 'username' as const,
				},
			],
			[t, goToProfile],
		);

		return (
			<View style={styles.container}>
				{!viewOnly && (
					<AssignButton
						organisationUuid={organisationUuid}
						teamUuid={teamUuid}
						type="careRecipients"
					/>
				)}
				<TeamMembersTable
					data={data}
					columnsConfig={columnsConfig}
					renderRowActions={renderRowActions}
				/>
			</View>
		);
	},
);

const styles = StyleSheet.create({
	container: {
		gap: spacing(2),
	},
});
